import { Helmet } from 'react-helmet-async';
import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { PROFESSIONAL_TERMS_MARKDOWN } from './professionalTermsMarkdownString';

/**
 * `react-markdown` não parseia tabelas GFM sem `remark-gfm`. Como evitamos
 * dependências novas, detectamos blocos de tabela direto na string do .md
 * e renderizamos manualmente, mantendo o restante do markdown intacto.
 */
type MarkdownSegment =
	| { kind: 'md'; text: string }
	| { kind: 'table'; headers: string[]; rows: string[][] };

const TABLE_REGEX =
	/(?:^|\n)(\|[^\n]+\|[\t ]*\n\|[\t :\-|]+\|[\t ]*(?:\n\|[^\n]+\|[\t ]*)+)/g;

const parseTableRow = (line: string): string[] =>
	line
		.replace(/^\s*\|/, '')
		.replace(/\|\s*$/, '')
		.split('|')
		.map((cell) => cell.trim());

const splitMarkdownByTables = (md: string): MarkdownSegment[] => {
	const segments: MarkdownSegment[] = [];
	let lastIndex = 0;
	let match: RegExpExecArray | null;

	TABLE_REGEX.lastIndex = 0;
	while ((match = TABLE_REGEX.exec(md)) !== null) {
		const tableBlock = match[1];
		const tableStart = match.index + match[0].length - tableBlock.length;

		if (tableStart > lastIndex) {
			segments.push({
				kind: 'md',
				text: md.slice(lastIndex, tableStart),
			});
		}

		const lines = tableBlock.trim().split('\n');
		const headers = parseTableRow(lines[0] ?? '');
		const rows = lines.slice(2).map(parseTableRow);

		segments.push({ kind: 'table', headers, rows });
		lastIndex = match.index + match[0].length;
	}

	if (lastIndex < md.length) {
		segments.push({ kind: 'md', text: md.slice(lastIndex) });
	}

	return segments;
};

const ProfessionalTermsOfUsePage = () => {
	const segments = useMemo(
		() => splitMarkdownByTables(PROFESSIONAL_TERMS_MARKDOWN),
		[],
	);

	return (
		<main className="mx-auto w-full max-w-3xl px-4 py-12 md:py-20">
			<Helmet>
				<title>Termos de Uso para Profissionais | Plumaa</title>
				<meta
					name="description"
					content="Regulamento e condições para especialistas parceiros da rede Plumaa. Diretrizes sobre prestação de serviços, ética e deveres contratuais."
				/>

				<meta property="og:type" content="website" />
				<meta
					property="og:title"
					content="Termos de Uso para Profissionais | Plumaa"
				/>
				<meta
					property="og:description"
					content="Conecte-se a pacientes de forma segura. Gestão completa de consultas online."
				/>

				<meta
					property="og:image"
					content="https://res.cloudinary.com/dd5pbp2tb/image/upload/v1765924080/Banner_rxsb8s.png"
				/>
				<meta
					property="og:image:secure_url"
					content="https://res.cloudinary.com/dd5pbp2tb/image/upload/v1765924080/Banner_rxsb8s.png"
				/>

				<meta property="og:image:type" content="image/png" />
				<meta property="og:image:width" content="1200" />
				<meta property="og:image:height" content="630" />
			</Helmet>

			<div className="space-y-6 text-justify text-zinc-600">
				{segments.map((segment, index) =>
					segment.kind === 'md' ? (
						<ReactMarkdown
							key={`md-${index}`}
							components={{
								h1: ({ ...props }) => (
									<h1
										className="mb-8 text-left text-2xl font-bold leading-tight tracking-normal text-gray-900 md:text-3xl"
										{...props}
									/>
								),
								h2: ({ ...props }) => (
									<h2
										className="pt-6 text-2xl font-semibold text-gray-800"
										{...props}
									/>
								),
								p: ({ ...props }) => (
									<p
										className="whitespace-pre-line text-zinc-600"
										{...props}
									/>
								),
								ul: ({ ...props }) => (
									<ul
										className="list-disc space-y-2 pl-5"
										{...props}
									/>
								),
								li: ({ ...props }) => (
									<li className="text-zinc-600" {...props} />
								),
								hr: () => (
									<div
										className="my-2 h-px w-full bg-transparent"
										aria-hidden
									/>
								),
								strong: ({ ...props }) => (
									<strong
										className="font-semibold text-gray-800"
										{...props}
									/>
								),
								em: ({ ...props }) => (
									<em className="italic" {...props} />
								),
								code: ({ ...props }) => (
									<code
										className="rounded bg-zinc-100 px-1 py-0.5 font-mono text-[0.92em] text-zinc-800"
										{...props}
									/>
								),
								table: ({ ...props }) => (
									<div className="overflow-x-auto py-1">
										<table
											className="w-full min-w-[720px] border-collapse border border-zinc-200 text-left text-sm"
											{...props}
										/>
									</div>
								),
								thead: ({ ...props }) => (
									<thead className="bg-zinc-100" {...props} />
								),
								tbody: ({ ...props }) => <tbody {...props} />,
								tr: ({ ...props }) => (
									<tr className="align-top" {...props} />
								),
								th: ({ ...props }) => (
									<th
										className="border border-zinc-200 px-3 py-2 font-semibold text-zinc-700"
										{...props}
									/>
								),
								td: ({ ...props }) => (
									<td
										className="border border-zinc-200 px-3 py-2 text-zinc-600"
										{...props}
									/>
								),
							}}
						>
							{segment.text}
						</ReactMarkdown>
					) : (
						<div
							key={`table-${index}`}
							className="overflow-x-auto py-1 text-left"
						>
							<table className="w-full min-w-[720px] border-collapse border border-zinc-200 text-left text-sm">
								<thead className="bg-zinc-100">
									<tr className="align-top">
										{segment.headers.map((header, hIdx) => (
											<th
												key={hIdx}
												className="border border-zinc-200 px-3 py-2 font-semibold text-zinc-700"
											>
												{header}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{segment.rows.map((row, rIdx) => (
										<tr key={rIdx} className="align-top">
											{row.map((cell, cIdx) => (
												<td
													key={cIdx}
													className="border border-zinc-200 px-3 py-2 text-zinc-600"
												>
													{cell}
												</td>
											))}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					),
				)}
			</div>
		</main>
	);
};

export default ProfessionalTermsOfUsePage;
